<template>
	<FloatingPanel
		:title="labels.title"
		@close="close"
	>
		<template #body>
			<div class="tag-details--header--title">
				<h3>{{ transTagLabel(tagName, sportName, eventTag.tag ? eventTag.tag.type : null) }}</h3>
				<span class="tag-icon">
					<Icon
						:icon-name="`tag-${tagName}`"
						size="lg"
					/>
				</span>
				<span class="tag-details--header--duration">{{ durationFormatted }}</span>
			</div>
			<div
				class="tag-details--header--subtitle"
				v-html="subtitle"
			/>

			<!-- form body -->
			<div class="tag-details--body">
				<div class="form-group tag-type-group">
					<FormField :label="labels.tagType">
						<Dropdown
							v-model="tag"
							:is-small="true"
							:options="tagTypeOptions"
							:disabled="isDisabled"
						/>
					</FormField>

					<FormField
						v-if="isNameInputActive"
						:error="validations.name"
					>
						<input
							v-model="name"
							class="form-input"
							type="text"
							:class="{
								error: validations.name,
							}"
							:disabled="isDisabled"
						>
					</FormField>
				</div>

				<div
					v-if="isAthleteFieldActive"
					class="form-group athlete-group"
				>
					<FormField :label="labels.athlete">
						<Dropdown
							v-model="player"
							:is-small="true"
							:options="participantsOptions"
							:disabled="isDisabled"
						/>
					</FormField>
				</div>

				<div
					v-if="isTeamFieldActive"
					class="form-group team-group"
				>
					<FormField :label="labels.team">
						<Dropdown
							v-model="team"
							:is-small="true"
							:options="participantsOptions"
							:disabled="isDisabled"
						/>
					</FormField>
				</div>
				<div
					v-if="isTeamFieldActive"
					class="form-group player-group"
				>
					<FormField :label="labels.player">
						<Dropdown
							v-model="player"
							:is-small="true"
							:options="playerOptions"
							:disabled="isDisabled"
						/>
					</FormField>
				</div>

				<div class="form-group time-group">
					<FormField
						:label="labels.eventMinute"
						:error="validations.timestamp"
					>
						<TimeInput
							:value="eventTag.timestamp"
							align="left"
							:is-valid="!validations.timestamp"
							data-qa="event-minute"
							:disabled="isDisabled"
							:readonly="isMobileWebApp"
							@input="handleTimestampChange"
							@click.native="showMobileTimePickerForTimestamp"
						/>
					</FormField>
					<FormField :label="labels.gameMinute">
						<TimeInput
							:value="gameTimeValue"
							align="left"
							is-valid
							data-qa="game-minute"
							disabled
							readonly
						/>
					</FormField>
				</div>

				<div class="form-group time-group">
					<FormField
						v-if="!isTimingTag"
						:label="labels.timeIn"
					>
						<TimeInput
							:value="timeIn"
							align="right"
							:is-valid="!validations.timeIn"
							:disabled="isDisabled"
							:readonly="isMobileWebApp"
							data-qa="time-in"
							@input="handleTimeInChange"
							@click.native="showMobileTimePickerForTimeIn"
						>
							<template #left>
								<Icon
									icon-name="pointing-left"
									size="lg"
								/>
							</template>
						</TimeInput>
					</FormField>
					<FormField
						v-if="!isTimingTag"
						:label="labels.timeOut"
						:error="validations.timeOut"
					>
						<TimeInput
							:value="timeOut"
							align="left"
							:is-valid="!validations.timeOut"
							:disabled="isDisabled"
							:readonly="isMobileWebApp"
							data-qa="time-out"
							@input="handleTimeOutChange"
							@click.native="showMobileTimePickerForTimeOut"
						>
							<template #right>
								<Icon
									icon-name="pointing-right"
									size="lg"
								/>
							</template>
						</TimeInput>
					</FormField>
				</div>
				<div class="form-group rating-group">
					<FormField :label="labels.rating">
						<Rating
							v-if="!isDisabled"
							:value="eventTag.rating"
							@change="updateRating"
						/>
					</FormField>
				</div>
				<div class="form-group offset-group">
					<FormField
						:label="trans('one.edit_offset.label')"
						:info="trans('one.edit_offset.help')"
					>
						<SwitchInput
							v-model="editTimeshift"
						/>
					</FormField>
					<div
						v-if="editTimeshift"
						class="timeshifts mt-4"
					>
						<FormField
							:label="trans('one.edit_offset.current_offset')"
						>
							<TimeInput
								:value="event.tagOffsetSeconds"
								:is-valid="true"
								align="left"
								:readonly="true"
							/>
						</FormField>
						<FormField
							:label="trans('one.edit_offset.new_offset')"
						>
							<TimeInput
								:value="newEventOffset"
								:is-valid="true"
								align="left"
								:readonly="true"
							/>
						</FormField>
					</div>
				</div>
			</div>
		</template>
		<template
			v-if="!showConfirmation"
			#footer
		>
			<div class="form-actions">
				<button
					v-if="!hasChanges && canUseMultiFormatTool && !hideMultiformatEditor"
					:disabled="!event.downloadable"
					class="button button--secondary"
					@click="openMultiformatEditor"
				>
					{{ $I18n.trans('event.multiformat_editor.title') }}
				</button>
				<DiscardButton
					v-if="hasChanges"
					:disabled="!hasChanges"
					@click.native="discardSingleEdit()"
				/>
				<button
					class="button button--primary"
					data-qa="save-tag-button"
					:disabled="!canSave"
					@click="save"
				>
					{{ transUcfirst("commons.save") }}
				</button>
			</div>
		</template>

		<template
			v-else
			#footer-overlay
		>
			<div class="form-confirm-box">
				<p class="description">
					{{ transUcfirst("commons.save_confirmation") }}
				</p>
				<div class="form-actions">
					<button
						class="button h-width-full button--secondary"
						@click="handleDiscard"
					>
						{{ transUcfirst("commons.dont_save") }}
					</button>
					<button
						class="button h-width-full button--primary"
						@click="handleConfirmSave"
					>
						{{ transUcfirst("commons.yes_please_save") }}
					</button>
				</div>
			</div>
		</template>
	</FloatingPanel>
</template>

<script>
import FloatingPanel from '../Panels/FloatingPanel';
import { mapActions, mapGetters } from 'vuex';
import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';
import FormField from '../Form/FormField';
import Dropdown from '@components/dropdown';
import TimeInput from '@components/TimeInput/TimeInput';
import SwitchInput from '@videotools/js/components/Switch/Switch';
import $I18n from '../../../i18n';
import { bus, EVENT, playerBus } from '@videotools/js/events';
import DiscardButton from '@components/buttons/DiscardButton.vue';
import { TAG_NAME_CLOCK_STOP, TAG_NAME_CLOCK_SET, TAG_NAME_CLOCK_START } from '@videotools/js/api/entities/TagButton';
import { GAME_SPORT } from '@shared/constants/game';
import { getGameTime } from '@videotools/js/logic/game';
import Rating from '@videotools/js/one/components/Rating/Rating';
import laroute from '@laroute';

const CONFIRMATION_NONE = 'none';
const CONFIRMATION_REQUIRED = 'required';
const CONFIRMATION_CONFIRMED = 'confirmed';

const TAGS_WITH_EDITABLE_NAME = new Set(['custom', 'cut']);

const CLOCK_TAGS = [
	TAG_NAME_CLOCK_STOP,
	TAG_NAME_CLOCK_SET,
	TAG_NAME_CLOCK_START,
];

const timestampToMinutesSeconds = (val) => {
	const minutes = Math.floor(val / 60);
	const seconds = val - minutes * 60;
	return { minutes, seconds };
};

const minutesSecondsToTimestamp = (minutes, seconds) => {
	return minutes * 60 + seconds;
};

export default {
	name:       'EventTagEdit',
	components: { TimeInput, FormField, FloatingPanel, Dropdown, DiscardButton, SwitchInput, Rating },
	props:      {
		elapsed: {
			type:    Number,
			default: 0,
		},
		hideMultiformatEditor: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			currentEventTagId: null,
			initialTag:        null,
			initialTeam:       null,
			initialPlayer:     null,
			initialName:       null,
			initialTimeIn:     0,
			initialTimeOut:    0,
			initialTimestamp:  0,
			initialRating:     null,
			confirmStatus:     CONFIRMATION_NONE,
			isBusy:            false,
			editTimeshift:     false,
			validations:       {
				name:      '',
				timestamp: '',
				timeIn:    '',
				timeOut:   '',
			},
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:        'app/isMobileWebApp',
			isAppleDevice:         'device/isAppleDevice',
			event:                 'event/event',
			tags:                  'event/tags',
			eventParticipants:     'event/participants',
			sportName:             'event/sportName',
			sportParticipantType:  'event/participantType',
			eventTag:              'eventTagEdit/selectedTag',
			eventTargetTags:       'tags/eventTargetTags',
			participantTags:       'tags/participantTags',
			timingTags:            'tags/timingTags',
			isForEvent:            'tags/isForEvent',
			isForParticipant:      'tags/isForParticipant',
			isForTiming:           'tags/isForTiming',
			canEditEventTag:       'permissions/canEditEventTag',
			canUseMultiFormatTool: 'permissions/canUseMultiFormatTool',
			isPlayerCompetition:   'event/isPlayerCompetition',
			tagToStreamOffset:     'event/tagToStreamOffset',
		}),
		labels() {
			return {
				title:       `${this.$I18n.trans_choice('one.tag', 1)} ${this.$I18n.trans('commons.details')}`,
				tagType:     this.$I18n.trans('one.tag_type'),
				team:        this.$I18n.trans_choice('commons.team', 1),
				player:      this.$I18n.trans_choice('commons.player', 1),
				eventMinute: this.$I18n.trans('one.event_minute'),
				gameMinute:  this.$I18n.trans('one.game_minute'),
				timeIn:      this.$I18n.trans('one.time_in'),
				timeOut:     this.$I18n.trans('one.time_out'),
				athlete:     this.$I18n.trans_choice('commons.athlete', 1),
				rating:      this.$I18n.trans('commons.rating'),
			};
		},
		subtitle() {
			if (this.eventTag.team) {
				if (this.eventTag.playerName) {
					return `${this.eventTag.teamName} &bull; ${this.eventTag.playerName}`;
				}

				return this.eventTag.teamName;
			} else if (this.eventTag.playerName) {
				return this.eventTag.playerName;
			}

			return '';
		},
		name: {
			get() {
				return this.eventTag.name;
			},
			set(value) {
				this.updateSelectedTagProps({ name: value });
			},
		},
		tag: {
			get() {
				return this.eventTag.tag;
			},
			set(value) {
				this.updateSelectedTagProps({ tag: value });
			},
		},
		player: {
			get() {
				return this.eventTag.player;
			},
			set(value) {
				this.updateSelectedTagProps({ player: value });
			},
		},
		team: {
			get() {
				return this.eventTag.team;
			},
			set(value) {
				this.updateSelectedTagProps({ team: value });
			},
		},
		timeIn() {
			return this.eventTag.startTimestamp;
		},
		timeOut() {
			return this.eventTag.endTimestamp;
		},
		timestamp() {
			return this.eventTag.timestamp;
		},
		tagName() {
			return this.eventTag.name;
		},
		teamName() {
			return this.eventTag.teamName;
		},
		durationFormatted() {
			if (!this.eventTag) {
				return null;
			}
			return formatTimestampAsMinutesSeconds(this.eventTag.secondsBefore + this.eventTag.secondsAfter);
		},
		timestampOffset() {
			return this.timestamp - this.initialTimestamp;
		},
		newEventOffset() {
			return this.event.tagOffsetSeconds + this.timestampOffset;
		},
		tagTypeOptions() {
			const mapper = (tagButton) => {
				return {
					key:   `tag_${tagButton.tag.id}`,
					id:    tagButton.tag.id,
					label: this.transTagLabel(tagButton.name, this.sportName, tagButton.type),
					value: tagButton.tag,
				};
			};

			if (this.isTimingTag) {
				return this.timingTags.map(mapper);
			}
			if (this.isParticipantTargetTag) {
				return this.participantTags.map(mapper);
			}
			if (this.isEventTargetTag) {
				return this.eventTargetTags.map(mapper);
			}

			return [];
		},
		participantsOptions() {
			return this.eventParticipants.map((participant) => {
				return {
					key:   `participant_${participant.id}`,
					id:    participant.id,
					label: participant.club ? participant.club.name : participant.name,
					value: participant,
				};
			});
		},
		playerOptions() {
			if (!this.team) {
				return [];
			}
			const team = this.eventParticipants.find((participant) => participant.id === this.team.id);
			if (team && team.players) {
				return team.players.map((player) => {
					return {
						key:   `player_${player.id}`,
						id:    player.id,
						label: player.name,
						value: player,
					};
				});
			}
			return [];
		},
		isNameInputActive() {
			if (!this.tag) {
				return false;
			}
			return TAGS_WITH_EDITABLE_NAME.has(this.tag.name);
		},
		isEventTargetTag() {
			return this.isForEvent(this.tag);
		},
		isParticipantTargetTag() {
			return this.isForParticipant(this.tag) || Boolean(this.player);
		},
		isTimingTag() {
			return this.isForTiming(this.tag);
		},
		hasValidationsErrors() {
			return Object.keys(this.validations).reduce((acc, validationField) => {
				acc = acc || this.validations[validationField] !== '';
				return acc;
			}, false);
		},
		hasChanges() {
			const formValues = [
				[this.eventTag.player?.id, this.initialPlayer?.id],
				[this.eventTag.tag?.id, this.initialTag?.id],
				[this.eventTag.team?.id, this.initialTeam?.id],
				[this.eventTag.name, this.initialName],
				[this.eventTag.startTimestamp, this.initialTimeIn],
				[this.eventTag.endTimestamp, this.initialTimeOut],
				[this.eventTag.timestamp, this.initialTimestamp],
				[this.eventTag.rating, this.initialRating],
			];

			return !formValues.every((pairs) => {
				return pairs[0] === pairs[1];
			});
		},
		canSave() {
			return this.hasChanges && !this.hasValidationsErrors && !this.isBusy;
		},
		showConfirmation() {
			return this.confirmStatus !== CONFIRMATION_NONE;
		},
		isAthleteFieldActive() {
			return this.isPlayerCompetition && this.isParticipantTargetTag;
		},
		isTeamFieldActive() {
			return !this.isPlayerCompetition && this.isParticipantTargetTag;
		},
		isDisabled() {
			return !this.canEditEventTag(this.eventTag) ||
				(
					[GAME_SPORT.ICE_HOCKEY, GAME_SPORT.FLOORBALL].includes(this.sportName) &&
					CLOCK_TAGS.includes(this.eventTag.tag.name)
				);
		},
		gameTimeValue() {
			const position = this.eventTag.timestamp;
			const { minutes, seconds } = getGameTime({ position, event: this.event, tags: this.tags });
			return minutes * 60 + seconds;
		},
	},
	watch: {
		eventTag(eventTag) {
			if (eventTag.id !== this.currentEventTagId) {
				this.initFields();
			}
		},
		name(value) {
			if (value.length > 70) {
				this.validations.name = $I18n.trans('validation.max.string', { attribute: 'Name', max: 70 });
			} else {
				this.validations.name = '';
			}
		},
	},
	created() {
		this.initFields();
		bus.subscribe(EVENT.TIMELINE_EDIT_UPDATE, this.handleTimelineEdit);
		bus.subscribe(EVENT.TAG_DELETE, this.handleTagDelete);
	},
	destroyed() {
		bus.unsubscribe(EVENT.TIMELINE_EDIT_UPDATE, this.handleTimelineEdit);
		bus.unsubscribe(EVENT.TAG_DELETE, this.handleTagDelete);
	},
	methods: {
		...mapActions({
			updateEventTag:         'event/updateEventTag',
			clearSelection:         'eventTagEdit/clearSelection',
			updateSelectedTagProps: 'eventTagEdit/updateSelectedTagProps',
			discardSingleEdit:      'eventTagEdit/discardSingleEdit',
			updateEventOffset:      'event/updateEventOffset',
		}),
		initFields() {
			if (!this.eventTag) return;

			this.currentEventTagId = this.eventTag.id;
			this.initialTag = this.eventTag.tag;
			this.initialName = this.eventTag.name;
			this.initTimeFields();

			if (this.isPlayerCompetition) {
				this.initialPlayer = this.eventTag.player;
			} else {
				this.initialTeam = this.eventTag.team;
				this.initialPlayer = this.eventTag.player;
			}

			this.initialRating = this.eventTag.rating;

			this.confirmStatus = CONFIRMATION_NONE;
		},
		initTimeFields() {
			if (!this.eventTag) return;
			this.initialTimeIn = this.eventTag.startTimestamp;
			this.initialTimeOut = this.eventTag.endTimestamp;
			this.initialTimestamp = this.eventTag.timestamp;
		},
		async save() {
			if (!this.hasChanges) {
				return;
			}
			const { hasValidationsErrors, timestamp } = this;
			if (hasValidationsErrors) {
				return;
			}

			if (this.editTimeshift) {
				this.isBusy = true;

				try {
					await this.updateEventOffset({
						eventId: this.event.id,
						offset:  this.newEventOffset,
					});

					this.initFields();

					bus.publish(
						EVENT.SUCCESS,
						$I18n.trans('one.edit_offset.success'),
					);

					this.clearSelection();
				} catch (error) {
					console.error(error);
					bus.publish(
						EVENT.ERROR,
						$I18n.trans('one.edit_offset.error'),
					);
				} finally {
					this.isBusy = false;
				}

				return;
			}

			let timing = {};

			if (this.isTimingTag) {
				// zero duration tag
				timing = {
					timestamp: timestamp + this.tagToStreamOffset,
				};
			} else {
				const secondsBefore = this.eventTag.secondsBefore;
				const secondsAfter = this.eventTag.secondsAfter;

				timing = {
					secondsBefore,
					secondsAfter,
					timestamp: timestamp + this.tagToStreamOffset,
				};
			}

			const payload = {
				tag:    this.tag,
				player: this.player,
				rating: this.eventTag.rating,
				...timing,
			};

			if (TAGS_WITH_EDITABLE_NAME.has(this.tag.name)) {
				payload.name = this.name.length > 0 ? this.name : null;
			}

			if (!this.isPlayerCompetition) payload.team = this.team;
			this.applyChanges(payload);
		},
		async applyChanges(data) {
			this.isBusy = true;
			const { name, player, secondsAfter, secondsBefore, team, timestamp, tag, rating } = data;
			const body = {};

			let needsUpdate = false;

			const tagId = tag.id;
			if (tagId) {
				body.tagId = tagId;
				needsUpdate = true;
			}

			if (team) {
				const teamId = team.id;
				if (teamId) {
					body.teamId = teamId;
					needsUpdate = true;
				}
			}

			if (player) {
				const playerId = player.id;
				if (playerId) {
					body.playerId = playerId;
					needsUpdate = true;
				}
			}

			if (name !== undefined) {
				body.name = name?.trim() || null;
				needsUpdate = true;
			}

			if (rating !== undefined) {
				body.rating = rating;
				needsUpdate = true;
			}

			if (timestamp) {
				body.offsetRelevant = false; // set too false to ignore tag_offset_seconds from event
				body.timestamp = timestamp;
				body.secondsBefore = secondsBefore || 0;
				body.secondsAfter = secondsAfter || 0;
				needsUpdate = true;
			}

			if (needsUpdate) {
				try {
					await this.updateEventTag({
						eventId:     this.event.id,
						tagId:       this.eventTag.id,
						body,
						backendSnap: this.isAppleDevice,
					});
				} finally {
					this.isBusy = false;
				}
			}

			this.clearSelection();
		},
		close() {
			if (this.hasChanges && this.confirmStatus !== CONFIRMATION_CONFIRMED) {
				this.confirmStatus = CONFIRMATION_REQUIRED;
				return;
			}
			this.clearSelection();
		},
		handleTimelineEdit({ time, secondsBefore, secondsAfter }) {
			this.timestamp = time;
			this.timeIn = time - secondsBefore;
			this.timeOut = time + secondsAfter;
		},
		handleTagDelete(eventTag) {
			if (this.eventTag && this.eventTag.id === eventTag.id) {
				this.clearSelection();
			}
		},
		handleDiscard() {
			this.confirmStatus = CONFIRMATION_CONFIRMED;
			this.close();
		},
		handleConfirmSave() {
			this.confirmStatus = CONFIRMATION_CONFIRMED;
			this.save();
		},
		handleTimestampChange(value) {
			const min = this.eventTag.secondsBefore;
			const max = this.event.duration - this.eventTag.secondsAfter;
			const integerValue = Number.parseInt(value);
			if (integerValue < min || integerValue > max) {
				const minFormatted = formatTimestampAsMinutesSeconds(min);
				const maxFormatted = formatTimestampAsMinutesSeconds(max);
				this.validations.timestamp = $I18n.trans('validation.between.numeric', {
					attribute: this.labels.eventMinute,
					min:       minFormatted,
					max:       maxFormatted,
				});
			} else {
				this.validations.timestamp = '';
			}

			if (!this.hasValidationsErrors) {
				this.updateSelectedTagProps({
					timestamp: value,
				});
			}
		},
		handleTimeInChange(value) {
			const integerTimestamp = Number.parseInt(this.timestamp);
			const integerTimeIn = Number.parseInt(value);
			const integerTimeOut = Number.parseInt(this.timeOut);
			const min = formatTimestampAsMinutesSeconds(integerTimeIn);
			const max = formatTimestampAsMinutesSeconds(integerTimeOut);
			this.validations.timeOut = '';

			if (integerTimeIn > integerTimeOut) {
				this.validations.timeIn = $I18n.trans('validation.between.numeric', { attribute: this.labels.timeIn, min: '00:00', max });
			} else {
				this.validations.timeIn = '';
				if (integerTimestamp < integerTimeIn || integerTimestamp > integerTimeOut) {
					this.validations.timestamp = $I18n.trans('validation.between.numeric', { attribute: this.labels.eventMinute, min, max });
				} else {
					this.validations.timestamp = '';
				}
			}

			if (!this.hasValidationsErrors) {
				this.updateSelectedTagProps({ secondsBefore: this.timestamp - value });
			}
		},
		handleTimeOutChange(value) {
			let eventDuration = this.event.duration;
			if (this.isLive) {
				eventDuration = this.elapsed;
				try {
					const position = playerBus.position;
					if (position <= 0) {
						eventDuration += Math.abs(position);
					}
				} catch {}
			}

			const integerTimestamp = Number.parseInt(this.timestamp);
			const integerTimeIn = Number.parseInt(this.timeIn);
			const integerTimeOut = Number.parseInt(value);
			const integerEventDuration = Number.parseInt(eventDuration);
			const min = formatTimestampAsMinutesSeconds(integerTimeIn);
			this.validations.timeIn = '';

			if (integerTimeOut < integerTimeIn || integerTimeOut > integerEventDuration) {
				const max = formatTimestampAsMinutesSeconds(integerEventDuration);
				this.validations.timeOut = $I18n.trans('validation.between.numeric', { attribute: this.labels.timeOut, min, max });
			} else {
				this.validations.timeOut = '';
				const max = formatTimestampAsMinutesSeconds(integerTimeOut);
				if (integerTimestamp < integerTimeIn || integerTimestamp > integerTimeOut) {
					this.validations.timestamp = $I18n.trans('validation.between.numeric', { attribute: this.labels.eventMinute, min, max });
				} else {
					this.validations.timestamp = '';
				}
			}

			if (!this.hasValidationsErrors) {
				this.updateSelectedTagProps({ secondsAfter: value - this.timestamp });
			}
		},
		showMobileTimePickerForTimestamp() {
			const { minutes, seconds } = timestampToMinutesSeconds(this.eventTag.timestamp);
			const { minutes: maxMinutes } = timestampToMinutesSeconds(this.event.duration);

			this.showMobileTimePicker({
				title:    this.transUcfirst('commons.type_edit', { type: this.labels.eventMinute }),
				minutes,
				seconds,
				maxMinutes,
				onChange: (minutes, seconds) => {
					this.handleTimestampChange(minutesSecondsToTimestamp(minutes, seconds));
				},
			});
		},
		showMobileTimePickerForTimeIn() {
			const { minutes, seconds } = timestampToMinutesSeconds(this.timeIn);
			const { minutes: maxMinutes } = timestampToMinutesSeconds(this.event.duration);

			this.showMobileTimePicker({
				title:    this.transUcfirst('commons.type_edit', { type: this.labels.timeIn }),
				minutes,
				seconds,
				maxMinutes,
				onChange: (minutes, seconds) => {
					this.handleTimeInChange(minutesSecondsToTimestamp(minutes, seconds));
				},
			});
		},
		showMobileTimePickerForTimeOut() {
			const { minutes, seconds } = timestampToMinutesSeconds(this.timeOut);
			const { minutes: maxMinutes } = timestampToMinutesSeconds(this.event.duration);

			this.showMobileTimePicker({
				title:    this.transUcfirst('commons.type_edit', { type: this.labels.timeOut }),
				minutes,
				seconds,
				maxMinutes,
				onChange: (minutes, seconds) => {
					this.handleTimeOutChange(minutesSecondsToTimestamp(minutes, seconds));
				},
			});
		},
		showMobileTimePicker({ minutes, seconds, title, maxMinutes, onChange }) {
			if (!this.isMobileWebApp) {
				return;
			}
			bus.publish(EVENT.TIME_PICKER_MODAL_OPEN, {
				title,
				minutes,
				seconds,
				maxMinutes,
				onChange,
			});
		},
		updateRating(rating) {
			this.updateSelectedTagProps({
				rating: rating,
			});
		},
		openMultiformatEditor() {
			const route = `${laroute.route('manager.one', {
				event: this.event.id,
			})}/capture?eventTagId=${this.eventTag.id}`;
			window.open(route);
		},
	},
};
</script>

<style lang="scss" scoped>
@import "@videotools/sass/_fonts";
@import "@shared/sass/shared-variables";

.floating-panel::v-deep {
	.floating-panel--content {
		overflow-y: auto;
	}

	.floating-panel--header {
		padding-bottom: 0;
	}
}

.tag-details--header--title {
	display: flex;
	align-items: center;

	> * {
		margin-right: 8px;
	}

	.tag-details--header--duration {
		@extend .text-sm;
	}
}

.tag-details--header--subtitle {
	@extend .text-sm;
	color: var(--color-soft-white);
	margin-top: 3px;
}

.tag-details--body {
	display: flex;
	flex-direction: column;
	margin-top: 28px;

	.time-group {
		display: flex;

		> div {
			flex: 1 1 auto;
			width: calc(50% - 0.75rem);

			&:first-child {
				margin-right: 1.5rem;
			}
		}
	}

	.offset-group .timeshifts {
		display: flex;

		> div {
			flex: 1 1 auto;
			width: calc(50% - 0.75rem);

			&:first-child {
				margin-right: 1.5rem;
			}
		}
	}

	.is-mobile & {
		margin-bottom: 32px;
	}
}

.tag-icon {
	display: flex;
	align-items: center;
}

.tag-type-group {
	.is-mobile & {
		margin-top: 12px;
	}

	order: 1;
}

.athlete-group {
	order: 2;
}

.team-group {
	order: 2;
}

.player-group {
	order: 3;
}

.time-group {
	order: 4;
}

.time-group {
	order: 5;
}

.rating-group {
	order: 6;
}

.offset-group {
	order: 7;
}

.form-actions {
	justify-content: flex-end;
}
</style>
